/* ActivitiesCrudCard.tsx
    Une card affichant la gestion des activités
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Inclut l'avoir client associé dans l'export CSV
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { ActivityData, activitiesApi } from 'features/activities';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/** Formatte un montant en euros (fr-FR) */
const formatCurrency = (value: number | null | undefined): string =>
	new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
		Number(value ?? 0)
	);

interface ActivitiesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// ActivitiesCrudCard component
export const ActivitiesCrudCard: React.FC<ActivitiesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/activities',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ActivityData) => a.id }]
			: []),
		// Colonne "Affaire"
		...(extended
			? [
					{
						label: 'Affaire',
						field: 'project.referenceNumber',
						render: (a: ActivityData) =>
							a.project ? a.project.referenceNumber : '',
						exportValue: (a: ActivityData): string =>
							a.project?.referenceNumber ?? '',
						linkUrl: (a: ActivityData) => `/app/projects/${a.project?.id}/edit`,
					},
				]
			: []),
		// Colonne "Date de début" au format jour/mois/année + durée fournie par l'API (durationHours, calcul backend)
		{
			label: 'Date',
			field: 'beginDate',
			filterType: 'date',
			render: (a: ActivityData) => {
				if (!a.beginDate) return '';
				const dateStr = new Date(a.beginDate).toLocaleDateString('fr-FR');
				// Utilise la durée calculée côté backend (plus fiable que le calcul JS)
				if (a.durationHours != null) {
					return `${dateStr} (${Number(a.durationHours).toFixed(1)} h)`;
				}
				return dateStr;
			},
			exportValue: (a: ActivityData): string =>
				a.beginDate ? new Date(a.beginDate).toLocaleDateString('fr-FR') : '',
		},
		// Colonne "Origine"
		{
			label: 'Origine',
			field: 'origin',
			render: (a: ActivityData) => a.origin ?? '',
			exportValue: (a: ActivityData): string => a.origin ?? '',
		},
		// Colonne "Service" uniquement si extended
		...(extended
			? [
					{
						label: 'Service',
						field: 'service.label',
						render: (a: ActivityData) => a.service?.label ?? '',
						exportValue: (a: ActivityData): string => a.service?.label ?? '',
					},
				]
			: []),
		// Colonne "Tarif HT" uniquement si extended
		...(extended
			? [
					{
						label: 'Tarif HT',
						field: 'unitPriceExclTax',
						render: (a: ActivityData) => {
							const value = Number(a.unitPriceExclTax ?? 0);
							return value ? formatCurrency(value) : '';
						},
						exportValue: (a: ActivityData): number | string =>
							a.unitPriceExclTax != null ? Number(a.unitPriceExclTax) : '',
					},
				]
			: []),
		// Colonne "Facture client" uniquement si extended — lien vers la fiche facture
		...(extended
			? [
					{
						label: 'Facture',
						field: 'customerInvoice.referenceNumber',
						render: (a: ActivityData) =>
							a.customerInvoice?.referenceNumber ?? '—',
						exportValue: (a: ActivityData): string =>
							a.customerInvoice?.referenceNumber ?? '',
						linkUrl: (a: ActivityData) =>
							a.customerInvoice?.id
								? `/app/customerInvoices/${a.customerInvoice.id}/edit`
								: '',
					},
				]
			: []),
	];

	// Colonnes visibles uniquement dans l'export Excel
	const exportOnlyColumns = [
		// Durée en heures (calculée backend)
		{
			label: 'Durée (h)',
			exportValue: (a: ActivityData): number | string =>
				a.durationHours != null ? Number(a.durationHours) : '',
		},
		// Avoir client associé (si remboursement)
		{
			label: 'Avoir',
			exportValue: (a: ActivityData): string =>
				a.creditNote?.referenceNumber ?? '',
		},
		// En mode sm, tarif et facture ne sont pas des colonnes → les exporter
		...(!extended
			? [
					{
						label: 'Tarif HT',
						exportValue: (a: ActivityData): number | string =>
							a.unitPriceExclTax != null ? Number(a.unitPriceExclTax) : '',
					},
					{
						label: 'Facture',
						exportValue: (a: ActivityData): string =>
							a.customerInvoice?.referenceNumber ?? '',
					},
				]
			: []),
		// Créé par
		{
			label: 'Créé par',
			exportValue: (a: ActivityData): string => {
				const u = a.createdBy;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		// Créé le
		{
			label: 'Créé le',
			exportValue: (a: ActivityData): string =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '',
		},
	];

	return (
		<GenericCrudCard<ActivityData>
			queryKey="activities"
			title={size === 'sm' ? 'Activités' : 'Liste des activités'}
			fetchData={(token, options) =>
				activitiesApi.listPaginated(token, options)
			}
			deleteItem={activitiesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter une activité"
			getAddPath={() => '/app/activities/new'}
			getEditPath={(id) => `/app/activities/${id}/edit`}
			emptyMessage="Aucune activité trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faChartLine : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default ActivitiesCrudCard;
